import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { PasswordInput } from "../components/PasswordInput";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, SignIn, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_COLORS = {
  admin: "bg-violet-100 text-violet-700", ops: "bg-blue-100 text-blue-700",
  finance: "bg-emerald-100 text-emerald-700", partner: "bg-amber-100 text-amber-700",
  customer: "bg-slate-100 text-slate-700",
};
const EMPTY = { name: "", email: "", password: "", role: "ops" };

export default function Users() {
  const { user, impersonate } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => { api.get("/users").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    try {
      await api.post("/users", form);
      toast.success(`User ${form.email} created`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const switchTo = async (u) => {
    if (u.id === user?.id) { toast.message("You're already signed in as this user"); return; }
    if (!window.confirm(`Switch into ${u.name} (${u.role})? You can return to your admin account anytime.`)) return;
    try { await impersonate(u.id); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shown = rows.filter((u) => filter === "all" || u.role === filter);

  return (
    <>
      <PageHeader title="Team / Users" subtitle="Create staff accounts & switch into any user"
        actions={<Button data-testid="add-user-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Create User</Button>} />
      <div className="p-8 space-y-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-sm w-48" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "admin", "ops", "finance", "partner", "customer"].map((r) =>
              <SelectItem key={r} value={r} className="capitalize">{r === "all" ? "All roles" : r}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody data-testid="users-table">
              {shown.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No users</TableCell></TableRow>}
              {shown.map((u) => (
                <TableRow key={u.id} className="hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-semibold flex items-center gap-2"><UsersThree size={15} className="text-muted-foreground" /> {u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><Badge className={`rounded-sm capitalize ${ROLE_COLORS[u.role] || ""}`}>{u.role}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-primary" onClick={() => switchTo(u)} data-testid={`switch-user-${u.id}`} disabled={u.id === user?.id}>
                      <SignIn size={15} weight="bold" /> Switch to
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="user-form">
          <SheetHeader><SheetTitle className="font-heading">Create User</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div><Label className="label-caps">Full Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Password</Label><PasswordInput data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div>
              <Label className="label-caps">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ops">Ops</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5">Module access for ops/finance is set in Roles & Access. Partners & customers are created via onboarding / self-registration.</p>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="user-save">Create User</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
